import React, { useState } from 'react';
import { Plus, Trash2, Upload, Image as ImageIcon } from 'lucide-react';
import { uploadApi } from '../../api';

/**
 * 规格编辑组件
 * specs: [{ name, name_en, name_km, values: [{ value, value_en, value_km }] }]
 * skus:  [{ price, vip_price, stock, image, sku_code, spec_values: [{ spec_index, value_index }] }]
 */
const ProductSpecEditor = ({ specs, skus, onChange, mainPrice, mainVipPrice }) => {

  const [uploadingSkuIndex, setUploadingSkuIndex] = useState(null);

  // 生成所有SKU组合
  const generateSkuCombinations = (newSpecs) => {
    if (!newSpecs || newSpecs.length === 0) return [];

    const validSpecs = newSpecs.filter(s => s.values && s.values.length > 0);
    if (validSpecs.length === 0) return [];

    // 笛卡尔积
    let combinations = [[]];
    for (let si = 0; si < newSpecs.length; si++) {
      const spec = newSpecs[si];
      if (!spec.values || spec.values.length === 0) continue;
      const newCombinations = [];
      for (const combo of combinations) {
        for (let vi = 0; vi < spec.values.length; vi++) {
          newCombinations.push([...combo, { spec_index: si, value_index: vi }]);
        }
      }
      combinations = newCombinations;
    }

    return combinations.map(spec_values => ({
      price:       '',
      vip_price:   '',
      stock:       0,
      image:       '',
      sku_code:    '',
      spec_values,
    }));
  };

  // 添加规格类型
  const addSpec = () => {
    const newSpecs = [...specs, { name: '', name_en: '', name_km: '', values: [] }];
    const newSkus  = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 删除规格类型
  const removeSpec = (si) => {
    const newSpecs = specs.filter((_, i) => i !== si);
    const newSkus  = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 更新规格类型名称
  const updateSpec = (si, field, value) => {
    const newSpecs = specs.map((s, i) => i === si ? { ...s, [field]: value } : s);
    onChange(newSpecs, skus);
  };

  // 添加规格值
  const addSpecValue = (si) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      return { ...s, values: [...(s.values || []), { value: '', value_en: '', value_km: '' }] };
    });
    const newSkus = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 删除规格值
  const removeSpecValue = (si, vi) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      return { ...s, values: s.values.filter((_, j) => j !== vi) };
    });
    const newSkus = generateSkuCombinations(newSpecs);
    onChange(newSpecs, newSkus);
  };

  // 更新规格值
  const updateSpecValue = (si, vi, field, value) => {
    const newSpecs = specs.map((s, i) => {
      if (i !== si) return s;
      const newValues = s.values.map((v, j) => j === vi ? { ...v, [field]: value } : v);
      return { ...s, values: newValues };
    });
    onChange(newSpecs, skus);
  };

  // 更新SKU字段
  const updateSku = (ki, field, value) => {
    const newSkus = skus.map((s, i) => i === ki ? { ...s, [field]: value } : s);
    onChange(specs, newSkus);
  };

  // 上传SKU图片
  const handleSkuImageUpload = async (ki, file) => {
    if (!file) return;
    setUploadingSkuIndex(ki);
    try {
      const res = await uploadApi.uploadProductImage(file);
      if (res?.data?.url) {
        updateSku(ki, 'image', res.data.url);
      }
    } catch (err) {
      alert('图片上传失败: ' + err.message);
    } finally {
      setUploadingSkuIndex(null);
    }
  };

  // 获取SKU标签（如：红色/S）
  const getSkuLabel = (sku) => {
    if (!sku.spec_values || sku.spec_values.length === 0) return '-';
    return sku.spec_values.map(sv => {
      const spec  = specs[sv.spec_index];
      const value = spec?.values?.[sv.value_index];
      return value?.value || '-';
    }).join(' / ');
  };

  return (
    <div className="space-y-4">
      {/* 规格类型编辑 */}
      <div className="space-y-3">
        {specs.map((spec, si) => (
          <div key={si} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
            {/* 规格类型名称 */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-medium text-gray-700 w-16 flex-shrink-0">规格{si + 1}</span>
              <input
                type="text"
                value={spec.name}
                onChange={e => updateSpec(si, 'name', e.target.value)}
                placeholder="规格名称（如：颜色）"
                className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
              <input
                type="text"
                value={spec.name_en}
                onChange={e => updateSpec(si, 'name_en', e.target.value)}
                placeholder="English"
                className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              />
              <button type="button" onClick={() => removeSpec(si)}
                className="text-red-400 hover:text-red-600 flex-shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            {/* 规格值 */}
            <div className="flex flex-wrap gap-2 mb-2">
              {(spec.values || []).map((val, vi) => (
                <div key={vi} className="flex items-center gap-1 bg-white border rounded-lg px-2 py-1">
                  <input
                    type="text"
                    value={val.value}
                    onChange={e => updateSpecValue(si, vi, 'value', e.target.value)}
                    placeholder="规格值"
                    className="w-20 text-sm border-none outline-none"
                  />
                  <button type="button" onClick={() => removeSpecValue(si, vi)}
                    className="text-gray-400 hover:text-red-500">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addSpecValue(si)}
                className="flex items-center gap-1 px-3 py-1 border border-dashed border-blue-400 text-blue-500 rounded-lg text-sm hover:bg-blue-50">
                <Plus className="w-3 h-3" />添加规格值
              </button>
            </div>
          </div>
        ))}

        {specs.length < 2 && (
          <button type="button" onClick={addSpec}
            className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-400 text-gray-600 rounded-xl text-sm hover:bg-gray-50 w-full justify-center">
            <Plus className="w-4 h-4" />
            添加规格类型（最多2种）
          </button>
        )}
      </div>

      {/* SKU列表 */}
      {skus.length > 0 && (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-2">
            SKU 列表（共 {skus.length} 个组合）
          </p>
          <div className="border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">规格</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">
                    价格
                    <span className="text-xs text-gray-400 ml-1">（空=用主价 ${mainPrice || '-'}）</span>
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">VIP价</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">库存</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">图片</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {skus.map((sku, ki) => (
                  <tr key={ki} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {getSkuLabel(sku)}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" step="0.01" min="0"
                        value={sku.price}
                        onChange={e => updateSku(ki, 'price', e.target.value)}
                        placeholder={mainPrice || '同主价'}
                        className="w-24 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" step="0.01" min="0"
                        value={sku.vip_price}
                        onChange={e => updateSku(ki, 'vip_price', e.target.value)}
                        placeholder={mainVipPrice || '无'}
                        className="w-24 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="number" min="0" step="1"
                        value={sku.stock}
                        onChange={e => updateSku(ki, 'stock', e.target.value)}
                        className="w-20 px-2 py-1 border rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {sku.image ? (
                          <img src={sku.image} alt="sku"
                            className="w-8 h-8 object-cover rounded border" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-100 rounded border flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-gray-400" />
                          </div>
                        )}
                        <label className="cursor-pointer text-blue-500 hover:text-blue-700 text-xs">
                          <input type="file" accept="image/*" className="hidden"
                            onChange={e => handleSkuImageUpload(ki, e.target.files[0])}
                            disabled={uploadingSkuIndex !== null}
                          />
                          {uploadingSkuIndex === ki ? '上传中...' : '上传'}
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSpecEditor;